// Consent Context
// React context for managing privacy consent state

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { privacyService } from '../services/privacyService';
import { cookieConsentManager } from '../utils/cookieConsent';
import { createLogger } from '../utils/logger';
import type { ConsentPreferences, ConsentType, PrivacySettings } from '../types/privacy';
import { DEFAULT_CONSENT, isValidConsent } from '../constants/privacy';

const logger = createLogger({ module: 'ConsentContext' });

interface ConsentContextType {
  // Current consent state
  consent: ConsentPreferences;
  privacySettings: PrivacySettings | null;
  isLoading: boolean;
  
  // Consent management
  updateConsent: (consent: ConsentPreferences) => Promise<boolean>;
  checkConsent: (consentType: ConsentType) => boolean;
  hasConsent: (consentType: ConsentType) => boolean;
  
  // Banner state
  showConsentBanner: boolean;
  dismissBanner: () => void;
  
  // Utilities
  refreshConsent: () => Promise<void>;
  clearCache: () => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

interface ConsentProviderProps {
  children: React.ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [consent, setConsent] = useState<ConsentPreferences>(DEFAULT_CONSENT);
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showConsentBanner, setShowConsentBanner] = useState(false);

  // Load consent settings
  const loadConsent = useCallback(async () => {
    try {
      setIsLoading(true);
      
      if (user?.id) {
        // Authenticated user - load from database
        const settings = await privacyService.getUserPrivacySettings(user.id);
        
        if (settings) {
          // User has existing privacy settings in database
          setPrivacySettings(settings);
          setConsent(settings.consent);
          setShowConsentBanner(false);
          
          // IMPORTANT: Update the cookie to match the user's database preferences
          // This ensures the cookie reflects the user's actual settings when they're logged in
          cookieConsentManager.setConsentCookie(settings.consent);
          logger.debug('Updated cookie to match user database settings', { consent: settings.consent });
        } else {
          // No settings found - try to transfer anonymous consent, then show banner if needed
          const transferred = await privacyService.transferAnonymousConsent(user.id);
          
          if (transferred) {
            // If we successfully transferred anonymous consent, reload to get the new settings
            const newSettings = await privacyService.getUserPrivacySettings(user.id);
            if (newSettings) {
              setPrivacySettings(newSettings);
              setConsent(newSettings.consent);
              setShowConsentBanner(false);
              // Cookie was already cleared during transfer, so no need to update it
            } else {
              // Transfer failed, show banner
              setPrivacySettings(null);
              setConsent(DEFAULT_CONSENT);
              setShowConsentBanner(true);
            }
          } else {
            // No anonymous consent to transfer, show banner
            setPrivacySettings(null);
            setConsent(DEFAULT_CONSENT);
            setShowConsentBanner(true);
          }
        }
      } else {
        // Anonymous user - check cookies first, then localStorage as fallback
        const cookieConsent = cookieConsentManager.getConsentCookie();
        
        if (cookieConsent) {
          // Found valid cookie consent
          setConsent(cookieConsent);
          setShowConsentBanner(false);
          logger.debug('Loaded consent from cookie', { consent: cookieConsent });
        } else {
          // Check if we have any stored consent (cookie or localStorage)
          const hasStoredConsent = privacyService.hasAnonymousConsent();
          
          if (hasStoredConsent) {
            // Try to load from localStorage and migrate to cookie
            const hasStoredConsent = localStorage.getItem('careercard_anonymous_consent');
            if (hasStoredConsent) {
              try {
                const stored = JSON.parse(hasStoredConsent);
                if (isValidConsent(stored.consent)) {
                  setConsent(stored.consent);
                  setShowConsentBanner(false);
                  
                  // Attempt to migrate to cookie
                  if (cookieConsentManager.setConsentCookie(stored.consent)) {
                    localStorage.removeItem('careercard_anonymous_consent');
                    logger.info('Migrated consent from localStorage to cookie');
                  }
                } else {
                  setShowConsentBanner(true);
                }
              } catch {
                setShowConsentBanner(true);
              }
            } else {
              setShowConsentBanner(true);
            }
          } else {
            // No stored consent - show banner
            setShowConsentBanner(true);
          }
        }
        
        setPrivacySettings(null);
      }
    } catch (error) {
      logger.error('Error loading consent', error);
      setConsent(DEFAULT_CONSENT);
      setShowConsentBanner(true);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Update consent preferences
  const updateConsent = useCallback(async (newConsent: ConsentPreferences): Promise<boolean> => {
    try {
      if (!isValidConsent(newConsent)) {
        logger.warn('Invalid consent preferences provided');
        return false;
      }

      if (user?.id) {
        // Authenticated user - save to database
        const result = await privacyService.updatePrivacySettings(user.id, newConsent, {
          ip_address: undefined, // Could be obtained from request
          user_agent: navigator.userAgent
        });
        
        if (result.success && result.data) {
          setPrivacySettings(result.data);
          setConsent(newConsent);
          setShowConsentBanner(false);
          
          // IMPORTANT: Also update the cookie to match the user's new database preferences
          // This ensures the cookie stays in sync with the user's actual settings
          cookieConsentManager.setConsentCookie(newConsent);
          logger.info('Consent updated for authenticated user and cookie synchronized');
          return true;
        }
        
        return false;
      } else {
        // Anonymous user - save to cookie (with localStorage fallback)
        const success = privacyService.setAnonymousConsent(newConsent);
        
        if (success) {
          setConsent(newConsent);
          setShowConsentBanner(false);
          logger.info('Consent updated for anonymous user (cookie)');
          return true;
        }
        
        return false;
      }
    } catch (error) {
      logger.error('Error updating consent', error);
      return false;
    }
  }, [user?.id]);

  // Check if user has specific consent
  const checkConsent = useCallback((consentType: ConsentType): boolean => {
    return consent[consentType] === true;
  }, [consent]);

  // Alias for checkConsent
  const hasConsent = useCallback((consentType: ConsentType): boolean => {
    return checkConsent(consentType);
  }, [checkConsent]);

  // Dismiss consent banner
  const dismissBanner = useCallback(() => {
    setShowConsentBanner(false);
    // Store dismissal to prevent showing again this session
    sessionStorage.setItem('consent_banner_dismissed', 'true');
  }, []);

  // Refresh consent from source
  const refreshConsent = useCallback(async () => {
    await loadConsent();
  }, [loadConsent]);

  // Clear cache
  const clearCache = useCallback(() => {
    if (user?.id) {
      privacyService.clearCache(user.id);
    }
  }, [user?.id]);

  // Load consent on mount and user change
  useEffect(() => {
    loadConsent();
  }, [loadConsent]);

  // Check if banner was dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem('consent_banner_dismissed');
    if (dismissed && showConsentBanner) {
      setShowConsentBanner(false);
    }
  }, [showConsentBanner]);

  const contextValue: ConsentContextType = {
    consent,
    privacySettings,
    isLoading,
    updateConsent,
    checkConsent,
    hasConsent,
    showConsentBanner,
    dismissBanner,
    refreshConsent,
    clearCache
  };

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
};

// Hook to use consent context
export const useConsent = (): ConsentContextType => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
}; 